import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Volume2, VolumeX } from 'lucide-react';
import api, { assetUrl } from '../services/api';

type ReelCard = {
  id: string;
  heading: string;
  description: string;
  image: string;
  video: string;
  mediaType: 'image' | 'video';
  mediaFit: 'fit' | 'fill';
  category: string;
  isActive: boolean;
  displayOrder: number;
};

const HeroReelPage: React.FC = () => {
  const [cards, setCards] = useState<ReelCard[]>([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [controlsTop, setControlsTop] = useState(140);
  const [frameTop, setFrameTop] = useState(240);
  const [swipeDirection, setSwipeDirection] = useState(1);

  const isAnimatingRef = useRef(false);
  const previousActiveIndexRef = useRef(0);
  const controlsRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  useEffect(() => {
    const loadCards = async () => {
      try {
        const response = await api.heroApi.getAll();
        const rows = Array.isArray(response?.data) ? response.data : [];
        const mapped = rows
          .map((card: any) => ({
            id: String(card.id),
            heading: String(card.heading || card.title || 'Hero Frame'),
            description: String(card.description || card.subtitle || ''),
            image: assetUrl(card.background_image || card.image || card.image_url),
            video: assetUrl(card.video_url),
            mediaType:
              (card.use_video === true ||
                card.use_video === 1 ||
                card.use_video === '1' ||
                card.use_video === 'true') && card.video_url
                ? 'video'
                : 'image',
            mediaFit: card.media_fit === 'fit' ? 'fit' : 'fill',
            category: String(card.category || card.type || 'General'),
            isActive:
              card.is_active === true ||
              card.is_active === 1 ||
              card.is_active === '1' ||
              card.is_active === 'true',
            displayOrder: Number(card.display_order || card.order || 0),
          }))
          .sort((a, b) => a.displayOrder - b.displayOrder);

        const activeCards = mapped.filter((card) => card.isActive);
        setCards(activeCards.length > 0 ? activeCards : mapped);
      } catch {
        setCards([]);
      }
    };

    loadCards();
  }, []);

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(cards.map((card) => card.category).filter(Boolean)))],
    [cards]
  );

  const filteredCards = useMemo(
    () => (activeCategory === 'All' ? cards : cards.filter((card) => card.category === activeCategory)),
    [activeCategory, cards]
  );

  useEffect(() => {
    setActiveIndex(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, filteredCards.length);
    videoRefs.current = videoRefs.current.slice(0, filteredCards.length);
  }, [filteredCards]);

  useEffect(() => {
    const previousIndex = previousActiveIndexRef.current;
    if (activeIndex !== previousIndex) {
      setSwipeDirection(activeIndex > previousIndex ? 1 : -1);
      previousActiveIndexRef.current = activeIndex;
    }
  }, [activeIndex]);

  useEffect(() => {
    const sections = sectionRefs.current.filter(Boolean) as HTMLElement[];

    if (sections.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!visibleEntry) {
          return;
        }

        const index = Number(visibleEntry.target.dataset.index || 0);
        if (!Number.isNaN(index)) {
          setActiveIndex(index);
        }
      },
      {
        threshold: [0.55, 0.7, 0.9],
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [filteredCards]);

  useEffect(() => {
    if (filteredCards.length === 0) {
      return;
    }

    const scrollToIndex = (nextIndex: number) => {
      if (isAnimatingRef.current) {
        return;
      }

      const target = sectionRefs.current[nextIndex];
      if (!target) {
        return;
      }

      isAnimatingRef.current = true;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => {
        isAnimatingRef.current = false;
      }, 700);
    };

    const handleWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) < 12 || isAnimatingRef.current) {
        return;
      }

      const nextIndex = event.deltaY > 0
        ? Math.min(activeIndex + 1, filteredCards.length - 1)
        : Math.max(activeIndex - 1, 0);

      if (nextIndex === activeIndex) {
        return;
      }

      event.preventDefault();
      scrollToIndex(nextIndex);
    };

    let touchStartY = 0;

    const handleTouchStart = (event: TouchEvent) => {
      touchStartY = event.touches[0]?.clientY ?? 0;
    };

    const handleTouchEnd = (event: TouchEvent) => {
      const touchEndY = event.changedTouches[0]?.clientY ?? touchStartY;
      const deltaY = touchStartY - touchEndY;

      if (Math.abs(deltaY) < 50 || isAnimatingRef.current) {
        return;
      }

      const nextIndex = deltaY > 0
        ? Math.min(activeIndex + 1, filteredCards.length - 1)
        : Math.max(activeIndex - 1, 0);

      if (nextIndex !== activeIndex) {
        scrollToIndex(nextIndex);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!['ArrowDown', 'PageDown', 'ArrowUp', 'PageUp', 'Space'].includes(event.code) || isAnimatingRef.current) {
        return;
      }

      const movingForward = event.code === 'ArrowDown' || event.code === 'PageDown' || (event.code === 'Space' && !event.shiftKey);
      const movingBackward = event.code === 'ArrowUp' || event.code === 'PageUp' || (event.code === 'Space' && event.shiftKey);

      let nextIndex = activeIndex;
      if (movingForward) {
        nextIndex = Math.min(activeIndex + 1, filteredCards.length - 1);
      } else if (movingBackward) {
        nextIndex = Math.max(activeIndex - 1, 0);
      }

      if (nextIndex === activeIndex) {
        return;
      }

      event.preventDefault();
      scrollToIndex(nextIndex);
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeIndex, filteredCards]);

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) {
        return;
      }

      video.muted = isMuted;

      if (index === activeIndex) {
        const playAttempt = video.play();
        if (playAttempt && typeof playAttempt.catch === 'function') {
          playAttempt.catch(() => undefined);
        }
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [activeIndex, isMuted, filteredCards]);

  useEffect(() => {
    const updateOffsets = () => {
      const nav = document.querySelector('nav.fixed');
      const navRect = nav?.getBoundingClientRect();
      const controlsRect = controlsRef.current?.getBoundingClientRect();
      const navBottom = Math.max(72, Math.ceil(navRect?.bottom ?? 88));
      const nextControlsTop = navBottom + 18;
      const controlsHeight = Math.ceil(controlsRect?.height ?? 60);

      setControlsTop(nextControlsTop);
      setFrameTop(nextControlsTop + controlsHeight + 22);
    };

    updateOffsets();

    const nav = document.querySelector('nav.fixed');
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => updateOffsets())
      : null;

    if (nav) {
      resizeObserver?.observe(nav);
    }
    if (controlsRef.current) {
      resizeObserver?.observe(controlsRef.current);
    }

    window.addEventListener('resize', updateOffsets);
    window.addEventListener('scroll', updateOffsets);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateOffsets);
      window.removeEventListener('scroll', updateOffsets);
    };
  }, [categories.length]);

  return (
    <main className="relative bg-black text-white">
      <div className="pointer-events-none fixed inset-x-0 top-0 z-30 h-40 bg-gradient-to-b from-black via-black/70 to-transparent" />
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-20 h-32 bg-gradient-to-t from-black via-black/80 to-transparent" />

      <div
        ref={controlsRef}
        className="fixed left-4 right-4 z-40 md:left-6 md:right-6"
        style={{ top: `${controlsTop}px` }}
      >
        <div className="w-full md:flex md:justify-end">
        <div className="flex w-full gap-2 overflow-x-auto whitespace-nowrap rounded-full border border-white/10 bg-black/55 p-2 backdrop-blur-md [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden md:w-auto md:max-w-[70vw] md:justify-end">
          {categories.map((category) => {
            const isActiveCategory = activeCategory === category;
            return (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                className={`shrink-0 snap-start rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.24em] transition-all ${
                  isActiveCategory
                    ? 'bg-[#FF6B35] text-white'
                    : 'bg-transparent text-white/60 hover:text-white'
                }`}
              >
                {category}
              </button>
            );
          })}
        </div>
        </div>
      </div>

      {filteredCards.length > 0 && (
        <div className="fixed right-4 top-1/2 z-40 hidden -translate-y-1/2 md:flex md:flex-col md:gap-3">
          {filteredCards.map((card, index) => (
            <button
              key={card.id}
              type="button"
              onClick={() => sectionRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="flex items-center gap-3 text-right"
            >
              <span
                className={`h-12 w-[3px] rounded-full transition-all ${
                  index === activeIndex ? 'bg-[#FF6B35]' : 'bg-white/20'
                }`}
              />
            </button>
          ))}
        </div>
      )}

      <div className="scroll-smooth">
        {filteredCards.length > 0 ? (
          filteredCards.map((card, index) => {
            const isCurrent = index === activeIndex;
            const isPassedCard = swipeDirection > 0 ? index < activeIndex : index > activeIndex;
            return (
              <section
                key={card.id}
                ref={(node) => {
                  sectionRefs.current[index] = node;
                }}
                data-index={index}
                className="relative flex min-h-screen items-end justify-center overflow-hidden"
                style={{ paddingTop: `${controlsTop}px` }}
              >
                <div className="absolute inset-0 bg-black" />
                <motion.div
                  className="absolute inset-x-0 bottom-4 flex justify-center px-6 md:bottom-8 md:px-[18%]"
                  style={{ top: `${frameTop}px` }}
                  initial={{
                    opacity: 0,
                    y: 56,
                    x: swipeDirection > 0 ? 76 : -76,
                    rotate: swipeDirection > 0 ? 2.2 : -2.2,
                    scale: 0.94,
                  }}
                  animate={
                    isCurrent
                      ? { opacity: 1, y: 0, x: 0, rotate: 0, scale: 1 }
                      : {
                          opacity: 0.58,
                          y: isPassedCard ? -30 : 30,
                          x: isPassedCard ? -20 : 20,
                          rotate: isPassedCard ? -1.4 : 1.4,
                          scale: 0.96,
                        }
                  }
                  transition={{ type: 'spring', stiffness: 165, damping: 24, mass: 0.9 }}
                >
                  <div className="relative h-full w-full max-w-[390px] overflow-hidden rounded-[3.3rem] bg-[linear-gradient(135deg,#ff4d00_0%,#ff6b1a_16%,#ff934d_38%,#ffd08f_52%,#ff934d_68%,#ff6b1a_84%,#ff4d00_100%)] p-[6px] shadow-[0_34px_140px_rgba(0,0,0,0.7)]">
                    <div className="absolute inset-[3px] rounded-[3.05rem] bg-[linear-gradient(180deg,rgba(255,140,82,0.28)_0%,rgba(255,107,26,0.12)_20%,rgba(8,8,8,1)_52%,rgba(5,5,5,1)_100%)]" />
                    <div className="pointer-events-none absolute inset-0 rounded-[3.3rem] ring-[3px] ring-[#ffb36f]/70 shadow-[0_0_0_2px_rgba(255,107,26,0.28),0_0_34px_rgba(255,107,26,0.35)]" />
                    <div className="pointer-events-none absolute inset-[14px] rounded-[2.6rem] border border-[#ffd08f]/26" />
                    <div className="relative h-full min-h-[72vh] w-full overflow-hidden rounded-[3.05rem] md:min-h-[76vh]">
                    <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center pt-3">
                      <div className="h-1.5 w-24 rounded-full bg-[#ff6b35]/55" />
                    </div>
                    <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-40 bg-gradient-to-b from-[#ff6b35]/18 via-transparent to-transparent" />
                    {card.mediaType === 'video' && card.video ? (
                      <motion.video
                        ref={(node) => {
                          videoRefs.current[index] = node;
                        }}
                        src={card.video}
                        className="h-full w-full object-contain bg-black"
                        playsInline
                        loop
                        muted={isMuted}
                        preload={isCurrent ? 'auto' : 'metadata'}
                        initial={{ scale: 1.1, x: swipeDirection > 0 ? 42 : -42, opacity: 0.45 }}
                        animate={
                          isCurrent
                            ? { scale: 1, x: 0, opacity: 1 }
                            : { scale: 1.05, x: isPassedCard ? -26 : 26, opacity: 0.5 }
                        }
                        transition={{ duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
                      />
                    ) : (
                      <motion.img
                        src={card.image}
                        alt={card.heading}
                        className={`h-full w-full ${card.mediaFit === 'fit' ? 'object-contain bg-black' : 'object-cover'}`}
                        initial={{ scale: 1.1, x: swipeDirection > 0 ? 42 : -42, opacity: 0.45 }}
                        animate={
                          isCurrent
                            ? { scale: 1, x: 0, opacity: 1 }
                            : { scale: 1.05, x: isPassedCard ? -24 : 24, opacity: 0.54 }
                        }
                        transition={{ duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
                      />
                    )}

                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.06)_0%,rgba(0,0,0,0.02)_30%,rgba(0,0,0,0.86)_100%)]" />

                    <motion.div
                      className="absolute inset-x-0 top-0 z-20 flex items-start justify-between p-5"
                      initial={{ opacity: 0, y: -18 }}
                      animate={isCurrent ? { opacity: 1, y: 0 } : { opacity: 0.68, y: -6 }}
                      transition={{ duration: 0.45, delay: 0.1 }}
                    >
                      <motion.span
                        className="rounded-full border border-[#ff6b35]/35 bg-black/45 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.34em] text-[#ffd6c7] backdrop-blur-sm"
                        initial={{ opacity: 0, x: -22 }}
                        animate={isCurrent ? { opacity: 1, x: 0 } : { opacity: 0.72, x: -8 }}
                        transition={{ duration: 0.45, delay: 0.12 }}
                      >
                        {card.category}
                      </motion.span>
                      {card.mediaType === 'video' && (
                        <motion.button
                          type="button"
                          onClick={() => setIsMuted((prev) => !prev)}
                          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/45 px-3 py-2 text-[10px] uppercase tracking-[0.24em] text-white/80 backdrop-blur-sm transition-colors hover:text-white"
                          initial={{ opacity: 0, x: 22 }}
                          animate={isCurrent ? { opacity: 1, x: 0 } : { opacity: 0.72, x: 8 }}
                          transition={{ duration: 0.45, delay: 0.14 }}
                        >
                          {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                          {isMuted ? 'Muted' : 'Sound'}
                        </motion.button>
                      )}
                    </motion.div>

                    <motion.div
                      className="absolute inset-x-0 bottom-0 z-20 p-5 sm:p-6"
                      initial={{ opacity: 0, y: 34, x: swipeDirection > 0 ? 30 : -30 }}
                      animate={
                        isCurrent
                          ? { opacity: 1, y: 0, x: 0 }
                          : { opacity: 0.72, y: isPassedCard ? -8 : 10, x: isPassedCard ? -10 : 10 }
                      }
                      transition={{ duration: 0.5, delay: 0.12 }}
                    >
                      <div className="p-1">
                        <motion.div
                          className="mb-3 h-[2px] w-16 rounded-full bg-gradient-to-r from-[#ff6b35] to-[#ff9a73]"
                          initial={{ scaleX: 0, transformOrigin: 'left' }}
                          animate={isCurrent ? { scaleX: 1 } : { scaleX: 0.65 }}
                          transition={{ duration: 0.45, delay: 0.2 }}
                        />
                        <motion.h1
                          className="text-2xl font-black uppercase leading-[0.92] tracking-tight text-white sm:text-3xl"
                          initial={{ opacity: 0, x: -26 }}
                          animate={isCurrent ? { opacity: 1, x: 0 } : { opacity: 0.82, x: -10 }}
                          transition={{ duration: 0.5, delay: 0.18 }}
                        >
                          {card.heading}
                        </motion.h1>
                        <motion.p
                          className="mt-3 text-sm leading-relaxed text-white/72 sm:text-[15px]"
                          initial={{ opacity: 0, x: 26 }}
                          animate={isCurrent ? { opacity: 1, x: 0 } : { opacity: 0.72, x: 10 }}
                          transition={{ duration: 0.5, delay: 0.22 }}
                        >
                          {card.description}
                        </motion.p>
                      </div>
                    </motion.div>
                    </div>
                  </div>
                </motion.div>

              </section>
            );
          })
        ) : (
          <section className="flex min-h-screen items-center justify-center px-6 text-center" style={{ paddingTop: `${frameTop}px` }}>
            <div>
              <p className="text-sm uppercase tracking-[0.38em] text-white/45">Hero Reel</p>
              <h1 className="mt-4 text-3xl font-black uppercase tracking-tight text-white">No reels found</h1>
              <p className="mt-3 text-white/60">Dashboard se active hero cards ya videos add karein.</p>
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default HeroReelPage;
